import { useState, useEffect } from 'react';
import {
  UserCircleIcon,
  XMarkIcon,
  PlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface Scorer {
  id: string;
  full_name: string;
  email: string;
}

interface Assignment {
  id: string;
  scorer_user_id: string;
  hole_number: number;
  scorer?: Scorer;
}

interface Props {
  tournamentId: string;
  roundId: string;
  roundName?: string;
  onClose: () => void;
}

const HOLES = Array.from({ length: 18 }, (_, i) => i + 1);

export default function ScorerAssignmentManager({ tournamentId, roundId, roundName, onClose }: Props) {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [scorers, setScorers] = useState<Scorer[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedScorerId, setSelectedScorerId] = useState('');
  const [selectedHoles, setSelectedHoles] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, [roundId]);

  const fetchUsersViaEdgeFunction = async (): Promise<Scorer[]> => {
    const apiUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const { data: { session } } = await supabase.auth.getSession();

    const res = await fetch(`${apiUrl}/functions/v1/invite-admin`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session?.access_token || anonKey}`,
        'Apikey': anonKey,
      },
    });

    if (!res.ok) return [];
    const json = await res.json();
    return (json.users ?? [])
      .filter((u: { is_active?: boolean }) => u.is_active !== false)
      .map((u: { id: string; full_name?: string; email?: string }) => ({
        id: u.id,
        full_name: u.full_name || '',
        email: u.email || '',
      }));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Use the invite-admin edge function (service role, bypasses RLS)
      const userRows = await fetchUsersViaEdgeFunction();

      const { data: assignRows } = await supabase
        .from('scorer_assignments')
        .select('id, scorer_user_id, hole_number')
        .eq('round_id', roundId)
        .order('hole_number');

      const scorerMap = new Map(userRows.map(s => [s.id, s]));
      setScorers(userRows);
      setAssignments(
        (assignRows ?? []).map(a => ({
          ...a,
          scorer: scorerMap.get(a.scorer_user_id),
        }))
      );
    } catch {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const toggleHole = (h: number) => {
    setSelectedHoles(prev => {
      const next = new Set(prev);
      if (next.has(h)) next.delete(h); else next.add(h);
      return next;
    });
  };

  const handleAssign = async () => {
    if (!selectedScorerId || selectedHoles.size === 0) return;
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const alreadyAssigned = new Set(
        assignments
          .filter(a => a.scorer_user_id === selectedScorerId)
          .map(a => a.hole_number)
      );
      const holesToInsert = [...selectedHoles].filter(h => !alreadyAssigned.has(h));

      if (holesToInsert.length === 0) {
        toast.error('All selected holes already assigned to this scorer');
        setSaving(false);
        return;
      }

      const rows = holesToInsert.map(h => ({
        tournament_id: tournamentId,
        round_id: roundId,
        scorer_user_id: selectedScorerId,
        hole_number: h,
        created_by: user?.id ?? null,
      }));

      const { error } = await supabase.from('scorer_assignments').insert(rows);
      if (error) throw error;

      toast.success(`Assigned ${holesToInsert.length} hole${holesToInsert.length > 1 ? 's' : ''}`);
      setSelectedScorerId('');
      setSelectedHoles(new Set());
      await loadData();
    } catch {
      toast.error('Failed to assign scorer');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (assignmentId: string) => {
    try {
      const { error } = await supabase.from('scorer_assignments').delete().eq('id', assignmentId);
      if (error) throw error;
      setAssignments(prev => prev.filter(a => a.id !== assignmentId));
      toast.success('Assignment removed');
    } catch {
      toast.error('Failed to remove assignment');
    }
  };

  const handleClearScorer = async (scorerId: string) => {
    const scorerAssignments = assignments.filter(a => a.scorer_user_id === scorerId);
    if (scorerAssignments.length === 0) return;
    try {
      const ids = scorerAssignments.map(a => a.id);
      const { error } = await supabase
        .from('scorer_assignments')
        .delete()
        .in('id', ids);
      if (error) throw error;
      setAssignments(prev => prev.filter(a => a.scorer_user_id !== scorerId));
      toast.success(`Cleared ${ids.length} assignment${ids.length > 1 ? 's' : ''}`);
    } catch {
      toast.error('Failed to clear assignments');
    }
  };

  const selectScorer = (id: string) => {
    setSelectedScorerId(id);
    setSearchQuery('');
  };

  // Group assignments by scorer
  const byScorerMap = new Map<string, Assignment[]>();
  for (const a of assignments) {
    if (!byScorerMap.has(a.scorer_user_id)) byScorerMap.set(a.scorer_user_id, []);
    byScorerMap.get(a.scorer_user_id)!.push(a);
  }

  const filteredScorers = scorers.filter(s => {
    const q = searchQuery.toLowerCase();
    return !q || s.full_name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q);
  });

  const selectedScorer = scorers.find(s => s.id === selectedScorerId);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0 bg-gradient-to-r from-gray-50 to-white">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Assign Scorers to Holes</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {roundName ? `${roundName} — ` : ''}Each scorer handles all groups at their assigned holes
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Loading users...</p>
            </div>
          ) : (
            <>
              {/* Current assignments grouped by scorer */}
              {byScorerMap.size > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Current Assignments</p>
                  <div className="space-y-3">
                    {[...byScorerMap.entries()].map(([scorerId, rows]) => {
                      const scorer = rows[0].scorer;
                      const holes = rows.map(r => r.hole_number).sort((a, b) => a - b);
                      return (
                        <div key={scorerId} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                              <UserCircleIcon className="w-5 h-5 text-amber-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {scorer?.full_name || 'Unknown'}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{scorer?.email}</p>
                            </div>
                            <span className="ml-auto text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                              {holes.length} hole{holes.length > 1 ? 's' : ''}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleClearScorer(scorerId)}
                              className="text-xs text-red-400 hover:text-red-600 font-medium ml-2 transition-colors"
                            >
                              Clear
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {holes.map(h => {
                              const assignment = rows.find(r => r.hole_number === h)!;
                              return (
                                <div key={h} className="group flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-red-200 transition-colors">
                                  <span className="text-xs font-bold text-gray-700">H{h}</span>
                                  <button
                                    onClick={() => handleRemove(assignment.id)}
                                    className="text-gray-300 group-hover:text-red-400 transition-colors"
                                  >
                                    <XMarkIcon className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {byScorerMap.size === 0 && (
                <div className="flex items-center gap-3 text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm font-medium">No holes assigned yet. Select a scorer below to get started.</p>
                </div>
              )}

              {/* Add new assignment */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Add Assignment</p>

                {/* Scorer selection */}
                {!selectedScorerId ? (
                  <div className="space-y-2">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search users by name or email..."
                        className="w-full text-sm border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 bg-white"
                      />
                    </div>

                    {scorers.length === 0 ? (
                      <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                        <UserCircleIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-500">No users available</p>
                        <p className="text-xs text-gray-400 mt-1">Add users in Settings to see them here.</p>
                      </div>
                    ) : (
                      <div className="border border-gray-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                        {filteredScorers.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">No users match your search</p>
                        ) : (
                          filteredScorers.map((s, idx) => {
                            const assignedHoles = assignments.filter(a => a.scorer_user_id === s.id).length;
                            return (
                              <button
                                key={s.id}
                                onClick={() => selectScorer(s.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-amber-50 transition-colors ${
                                  idx > 0 ? 'border-t border-gray-100' : ''
                                }`}
                              >
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold text-gray-600">
                                    {(s.full_name || s.email).charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">{s.full_name || 'Unnamed'}</p>
                                  <p className="text-xs text-gray-400 truncate">{s.email}</p>
                                </div>
                                {assignedHoles > 0 && (
                                  <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                                    {assignedHoles} assigned
                                  </span>
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Selected scorer pill */}
                    <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                      <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-amber-700">
                          {(selectedScorer?.full_name || selectedScorer?.email || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{selectedScorer?.full_name || 'Unnamed'}</p>
                        <p className="text-xs text-gray-500 truncate">{selectedScorer?.email}</p>
                      </div>
                      <button
                        onClick={() => { setSelectedScorerId(''); setSelectedHoles(new Set()); }}
                        className="text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
                      >
                        Change
                      </button>
                    </div>

                    {/* Hole selection grid */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-gray-600">Select holes to assign:</p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              const all = new Set(
                                HOLES.filter(h => !assignments.some(
                                  a => a.scorer_user_id === selectedScorerId && a.hole_number === h
                                ))
                              );
                              setSelectedHoles(all);
                            }}
                            className="text-xs text-amber-600 font-medium hover:text-amber-700"
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedHoles(new Set());
                            }}
                            className="text-xs text-gray-400 hover:text-gray-600"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-9 gap-1.5">
                        {HOLES.map(h => {
                          const alreadyAssigned = assignments.some(
                            a => a.scorer_user_id === selectedScorerId && a.hole_number === h
                          );
                          const isSelected = selectedHoles.has(h);
                          return (
                            <button
                              key={h}
                              onClick={() => !alreadyAssigned && toggleHole(h)}
                              disabled={alreadyAssigned}
                              className={`h-10 rounded-lg text-xs font-bold transition-all ${
                                alreadyAssigned
                                  ? 'bg-emerald-50 text-emerald-500 border border-emerald-200 cursor-not-allowed'
                                  : isSelected
                                  ? 'bg-amber-500 text-white shadow-sm shadow-amber-200'
                                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                              }`}
                            >
                              {alreadyAssigned ? <CheckCircleIcon className="w-4 h-4 mx-auto" /> : h}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Assign button */}
                    <button
                      onClick={handleAssign}
                      disabled={selectedHoles.size === 0 || saving}
                      className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold disabled:opacity-40 disabled:hover:bg-amber-500 flex items-center justify-center gap-2 transition-colors"
                    >
                      {saving ? (
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <PlusIcon className="w-4 h-4" />
                      )}
                      Assign {selectedHoles.size > 0 ? `${selectedHoles.size} Hole${selectedHoles.size > 1 ? 's' : ''}` : 'Holes'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex-shrink-0 border-t border-gray-100 pt-4 bg-gray-50/50">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-white transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
